import {
  AlphaColors,
  PrimitivesColors,
  SemanticColors,
  StateColors,
  UtilityColors,
} from "../types/colors";

export const primitivesColors: Readonly<PrimitivesColors> = {
  purple: {
    50: "#f8f6ff",
    100: "#eee9fe",
    200: "#d3c9fb",
    300: "#b5a5fa",
    400: "#a696fe",
    500: "#8167ff",
    600: "#745df4",
    700: "#6545e5",
    800: "#5e33d9",
    900: "#5430bb",
    1000: "#442698",
    1100: "#3a2578",
  },
  blue: {
    50: "#eff7ff",
    100: "#e1efff",
    200: "#bbdaff",
    300: "#96c6ff",
    400: "#71b2ff",
    500: "#2a8dff",
    600: "#0076ff",
    700: "#0057ff",
    800: "#1155cb",
    900: "#0937a5",
    1000: "#002888",
  },
  emerald: {
    50: "#e8fcfa",
    100: "#cdfdfa",
    200: "#9ee9e4",
    300: "#7ad9d3",
    400: "#5dbfb9",
    500: "#41a69e",
    600: "#2f8e88",
    700: "#1f7a74",
    800: "#13645f",
    900: "#0a4f4a",
    1000: "#033d38",
  },
  green: {
    50: "#f0fdf1",
    100: "#e5f6e6",
    200: "#c2e8c2",
    300: "#99d99a",
    400: "#6ccb70",
    500: "#42bd4c",
    600: "#05af25",
    700: "#02a31e",
    800: "#008200",
    900: "#006300",
    1000: "#004200",
  },
  orange: {
    50: "#fff7ed",
    100: "#ffedd5",
    200: "#fed7aa",
    300: "#fdba74",
    400: "#ff9741",
    500: "#ff722c",
    600: "#f36302",
    700: "#e04e00",
    800: "#b93d14",
    900: "#973414",
    1000: "#712308",
  },
  yellow: {
    50: "#fffaeb",
    100: "#fff7db",
    200: "#ffecb3",
    300: "#ffe082",
    400: "#ffd44f",
    500: "#ffc928",
    600: "#ffb201",
    700: "#e69800",
    800: "#b47002",
    900: "#854d0e",
    1000: "#713f12",
  },
  red: {
    50: "#fff5f5",
    100: "#feebeb",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f38181",
    500: "#f06060",
    600: "#f43333",
    700: "#de1c1c",
    800: "#b22424",
    900: "#8c1717",
    1000: "#5a0c0c",
  },
  rosewood: {
    50: "#fff4f8",
    100: "#fce9ef",
    200: "#fcdfe8",
    300: "#faccdc",
    400: "#fe9fbd",
    500: "#fb73a0",
    600: "#f7306f",
    700: "#e01854",
    800: "#c30a42",
    900: "#a30434",
    1000: "#800026",
  },
  grey: {
    0: "#ffffff",
    50: "#fcfcfc",
    100: "#fafafa",
    200: "#f5f5f5",
    300: "#eeeeee",
    400: "#dedede",
    500: "#c4c4c4",
    600: "#939393",
    700: "#757575",
    800: "#616161",
    900: "#424242",
    1000: "#212121",
    1100: "#0e0e0e",
  },
};

const alphaColors: Readonly<AlphaColors> = {
  white: {
    50: "#ffffff20",
    100: "#ffffff42",
    200: "#ffffff61",
    300: "#ffffff75",
    400: "#ffffff93",
    500: "#ffffffc4",
    600: "#ffffffde",
    700: "#ffffffed",
    800: "#fffffff5",
    900: "#fffffffa",
    1000: "#fffffffc",
  },
  grey: {
    50: "#0e0e0e03",
    100: "#0e0e0e05",
    200: "#0e0e0e0a",
    300: "#0e0e0e12",
    400: "#0e0e0e24",
    500: "#0e0e0e3f",
    600: "#0e0e0e72",
    700: "#0e0e0e92",
    800: "#0e0e0ea7",
    900: "#0e0e0ec8",
    1000: "#0e0e0eeb",
  },
};

const stateColors: Readonly<StateColors> = {
  brand: {
    base: primitivesColors.purple["700"],
    base_hover: primitivesColors.purple["800"],
    light: primitivesColors.purple["100"],
    light_hover: primitivesColors.purple["200"],
    dark: primitivesColors.purple["900"],
    dark_hover: primitivesColors.purple["1000"],
  },
  danger: {
    base: primitivesColors.red["700"],
    base_hover: primitivesColors.red["800"],
    light: primitivesColors.red["100"],
    light_hover: primitivesColors.red["200"],
    dark: primitivesColors.red["900"],
    dark_hover: primitivesColors.red["1000"],
  },
  information: {
    base: primitivesColors.blue["700"],
    base_hover: primitivesColors.blue["800"],
    light: primitivesColors.blue["100"],
    light_hover: primitivesColors.blue["200"],
    dark: primitivesColors.blue["900"],
    dark_hover: primitivesColors.blue["1000"],
  },
  neutral: {
    base: primitivesColors.grey["700"],
    base_hover: primitivesColors.grey["800"],
    light: primitivesColors.grey["100"],
    light_hover: primitivesColors.grey["200"],
    dark: primitivesColors.grey["900"],
    dark_hover: primitivesColors.grey["1000"],
  },
  positive: {
    base: primitivesColors.green["700"],
    base_hover: primitivesColors.green["800"],
    light: primitivesColors.green["100"],
    light_hover: primitivesColors.green["200"],
    dark: primitivesColors.green["800"],
    dark_hover: primitivesColors.green["900"],
  },
  alert: {
    base: primitivesColors.yellow["700"],
    base_hover: primitivesColors.yellow["900"],
    light: primitivesColors.yellow["50"],
    light_hover: primitivesColors.yellow["200"],
    dark: primitivesColors.yellow["800"],
    dark_hover: primitivesColors.yellow["1000"],
  },
  warning: {
    base: primitivesColors.orange["700"],
    base_hover: primitivesColors.orange["900"],
    light: primitivesColors.orange["50"],
    light_hover: primitivesColors.orange["200"],
    dark: primitivesColors.orange["800"],
    dark_hover: primitivesColors.orange["1000"],
  },
};

const semanticColors: Readonly<SemanticColors> = {
  bg: {
    primary: primitivesColors.grey["0"],
    primary_alpha: alphaColors.grey["50"],
    primary_hover: primitivesColors.grey["100"],
    primary_hover_brand: primitivesColors.purple["100"],
    secondary: primitivesColors.grey["100"],
    secondary_hover: primitivesColors.grey["100"],
    tertiary: primitivesColors.grey["200"],
    tertiary_hover: primitivesColors.grey["300"],
    quaternary: primitivesColors.grey["300"],
    quaternary_hover: primitivesColors.grey["400"],
    brand: primitivesColors.purple["800"],
    brand_hover: primitivesColors.purple["900"],
    brand_secondary: primitivesColors.purple["100"],
    brand_secondary_hover: primitivesColors.purple["200"],
    brand_tertiary: primitivesColors.purple["50"],
    brand_tertiary_hover: primitivesColors.purple["100"],
    positive: stateColors.positive["base"],
    positive_hover: stateColors.positive["base_hover"],
    positive_secondary: stateColors.positive["light"],
    positive_secondary_hover: stateColors.positive["light_hover"],
    negative: stateColors.danger["base"],
    negative_hover: stateColors.danger["base_hover"],
    negative_secondary: stateColors.danger["light"],
    negative_secondary_hover: stateColors.danger["light_hover"],
    alert: stateColors.alert["base"],
    alert_hover: stateColors.alert["base_hover"],
    alert_secondary: stateColors.alert["light"],
    alert_secondary_hover: stateColors.alert["light_hover"],
    warning: stateColors.warning["base"],
    warning_hover: stateColors.warning["base_hover"],
    warning_secondary: stateColors.warning["light"],
    warning_secondary_hover: stateColors.warning["light_hover"],
    info: stateColors.information["base"],
    info_hover: stateColors.information["base_hover"],
    info_secondary: stateColors.information["light"],
    info_secondary_hover: stateColors.information["light_hover"],
    disabled: primitivesColors.grey["200"],
    disabled_secondary: primitivesColors.grey["300"],
    disabled_alpha: alphaColors.grey["200"],
    disabled_white_alpha: alphaColors.white["200"],
    inverse: primitivesColors.grey["1100"],
    inverse_secondary: primitivesColors.grey["700"],
    inverse_tertiary: primitivesColors.grey["400"],
    rating_light: alphaColors.white["1000"],
    rating_dark: alphaColors.grey["900"],
    offer: primitivesColors.rosewood["600"],
    offer_light: primitivesColors.rosewood["200"],
    offer_darker: primitivesColors.rosewood["800"],
    pill_button: primitivesColors.grey["0"],
    overlay: alphaColors.grey["800"],
  },
  border: {
    primary: primitivesColors.grey["200"],
    primary_alpha: alphaColors.grey["200"],
    primary_hover: primitivesColors.grey["400"],
    secondary: primitivesColors.grey["300"],
    secondary_alpha: alphaColors.grey["300"],
    tertiary: primitivesColors.grey["400"],
    tertiary_alpha: alphaColors.grey["400"],
    quaternary: primitivesColors.grey["500"],
    quaternary_alpha: alphaColors.grey["500"],
    brand: primitivesColors.purple["800"],
    brand_secondary: primitivesColors.purple["300"],
    brand_tertiary: primitivesColors.purple["100"],
    positive: stateColors.positive["base"],
    positive_secondary: stateColors.positive["dark"],
    positive_tertiary: stateColors.positive["light"],
    negative: stateColors.danger["base"],
    negative_secondary: stateColors.danger["dark"],
    negative_tertiary: stateColors.danger["light"],
    alert: stateColors.alert["base"],
    alert_secondary: stateColors.alert["dark"],
    alert_tertiary: stateColors.alert["light"],
    warning: stateColors.warning["base"],
    warning_secondary: stateColors.warning["dark"],
    warning_tertiary: stateColors.warning["light"],
    information: stateColors.information["base"],
    information_secondary: stateColors.information["dark"],
    information_tertiary: stateColors.information["light"],
    inverse: primitivesColors.grey["1100"],
    on_dark_bg: primitivesColors.grey["0"],
    disabled: primitivesColors.grey["400"],
    disabled_alpha: alphaColors.grey["400"],
    disabled_white_alpha: alphaColors.white["300"],
    rating_light: alphaColors.grey["200"],
    rating_dark: alphaColors.white["200"],
  },
  icon: {
    primary: primitivesColors.grey["1000"],
    primary_hover: primitivesColors.grey["1100"],
    secondary: primitivesColors.grey["800"],
    secondary_alpha: alphaColors.grey["800"],
    secondary_hover: primitivesColors.grey["900"],
    tertiary: primitivesColors.grey["600"],
    tertiary_alpha: alphaColors.grey["600"],
    tertiary_hover: primitivesColors.grey["700"],
    quaternary: primitivesColors.grey["500"],
    quaternary_alpha: alphaColors.grey["500"],
    quaternary_hover: primitivesColors.grey["600"],
    on_dark_bg: primitivesColors.grey["0"],
    on_dark_bg_hover: primitivesColors.grey["100"],
    brand: primitivesColors.purple["800"],
    brand_secondary: primitivesColors.purple["300"],
    brand_tertiary: primitivesColors.purple["100"],
    brand_hover: primitivesColors.purple["900"],
    positive: stateColors.positive["base"],
    positive_hover: stateColors.positive["base_hover"],
    positive_dark: stateColors.positive["dark"],
    positive_dark_hover: stateColors.positive["dark_hover"],
    negative: stateColors.danger["base"],
    negative_hover: stateColors.danger["base_hover"],
    negative_dark: stateColors.danger["dark"],
    negative_dark_hover: stateColors.danger["dark_hover"],
    alert: stateColors.alert["base"],
    alert_hover: stateColors.alert["base_hover"],
    alert_dark: stateColors.alert["dark"],
    alert_dark_hover: stateColors.alert["dark_hover"],
    warning: stateColors.warning["base"],
    warning_hover: stateColors.warning["base_hover"],
    warning_dark: stateColors.warning["dark"],
    warning_dark_hover: stateColors.warning["dark_hover"],
    information: stateColors.information["base"],
    information_hover: stateColors.information["base_hover"],
    information_dark: stateColors.information["dark"],
    information_dark_hover: stateColors.information["dark_hover"],
    disabled: primitivesColors.grey["500"],
    disabled_alpha: alphaColors.grey["500"],
    disabled_white_alpha: alphaColors.white["300"],
    placeholder: primitivesColors.grey["600"],
    offer: primitivesColors.rosewood["600"],
    offer_light: primitivesColors.rosewood["200"],
    offer_dark: primitivesColors.rosewood["800"],
  },
  rating: {
    "5": primitivesColors.green["800"],
    "5_secondary": primitivesColors.green["100"],
    "4": primitivesColors.green["700"],
    "4_secondary": primitivesColors.green["200"],
    "3": primitivesColors.orange["400"],
    "3_secondary": primitivesColors.orange["100"],
    "2": primitivesColors.orange["600"],
    "2_secondary": primitivesColors.orange["50"],
    "1": primitivesColors.red["700"],
    "1_secondary": primitivesColors.red["50"],
    default: alphaColors.grey["300"],
  },
  text: {
    primary: primitivesColors.grey["1000"],
    primary_hover: primitivesColors.grey["1000"],
    secondary: primitivesColors.grey["800"],
    secondary_hover: primitivesColors.grey["900"],
    tertiary: primitivesColors.grey["600"],
    tertiary_hover: primitivesColors.grey["700"],
    on_dark_bg: primitivesColors.grey["0"],
    on_dark_bg_hover: primitivesColors.grey["100"],
    on_dark_bg_secondary: alphaColors.white["600"],
    brand: primitivesColors.purple["800"],
    brand_hover: primitivesColors.purple["900"],
    brand_secondary: primitivesColors.purple["300"],
    brand_secondary_hover: primitivesColors.purple["400"],
    link: primitivesColors.purple["800"],
    link_hover: primitivesColors.purple["900"],
    positive: stateColors.positive["base"],
    positive_hover: stateColors.positive["base_hover"],
    positive_dark: stateColors.positive["dark"],
    positive_dark_hover: stateColors.positive["dark_hover"],
    negative: stateColors.danger["base"],
    negative_hover: stateColors.danger["base_hover"],
    negative_dark: stateColors.danger["dark"],
    negative_dark_hover: stateColors.danger["dark_hover"],
    alert: stateColors.alert["base"],
    alert_hover: stateColors.alert["base_hover"],
    alert_dark: stateColors.alert["dark"],
    alert_dark_hover: stateColors.alert["dark_hover"],
    warning: stateColors.warning["base"],
    warning_hover: stateColors.warning["base_hover"],
    warning_dark: stateColors.warning["dark"],
    warning_dark_hover: stateColors.warning["dark_hover"],
    information: stateColors.information["base"],
    information_hover: stateColors.information["base_hover"],
    information_dark: stateColors.information["dark"],
    information_dark_hover: stateColors.information["dark_hover"],
    disabled: primitivesColors.grey["500"],
    disabled_alpha: alphaColors.grey["500"],
    disabled_white_alpha: alphaColors.white["300"],
    placeholder: primitivesColors.grey["600"],
    offer: primitivesColors.rosewood["600"],
    offer_light: primitivesColors.rosewood["200"],
    offer_dark: primitivesColors.rosewood["800"],
    quaternary: primitivesColors.grey["500"],
    quaternary_alpha: alphaColors.grey["500"],
  },
};

const utilityColors: Readonly<UtilityColors> = {
  text: {
    red_primary: primitivesColors.red["100"],
    red_secondary: primitivesColors.red["700"],
    red_tertiary: primitivesColors.red["1000"],
    blue_primary: primitivesColors.blue["100"],
    blue_secondary: primitivesColors.blue["700"],
    blue_tertiary: primitivesColors.blue["1000"],
    green_primary: primitivesColors.green["100"],
    green_secondary: primitivesColors.green["700"],
    green_tertiary: primitivesColors.green["1000"],
    emerald_primary: primitivesColors.emerald["100"],
    emerald_secondary: primitivesColors.emerald["700"],
    emerald_tertiary: primitivesColors.emerald["1000"],
    orange_primary: primitivesColors.orange["100"],
    orange_secondary: primitivesColors.orange["700"],
    orange_tertiary: primitivesColors.orange["1000"],
    yellow_primary: primitivesColors.yellow["100"],
    yellow_secondary: primitivesColors.yellow["700"],
    yellow_tertiary: primitivesColors.yellow["1000"],
    rosewood_primary: primitivesColors.rosewood["100"],
    rosewood_secondary: primitivesColors.rosewood["700"],
    rosewood_tertiary: primitivesColors.rosewood["1000"],
    purple_primary: primitivesColors.purple["100"],
    purple_secondary: primitivesColors.purple["700"],
    purple_tertiary: primitivesColors.purple["1100"],
    grey_alpha_primary: alphaColors.grey["100"],
    grey_alpha_secondary: alphaColors.grey["700"],
    grey_alpha_tertiary: alphaColors.grey["1000"],
    white_alpha_primary: alphaColors.white["100"],
    white_alpha_secondary: alphaColors.white["700"],
    white_alpha_tertiary: alphaColors.white["1000"],
  },
  icon: {
    red_primary: primitivesColors.red["100"],
    red_secondary: primitivesColors.red["700"],
    red_tertiary: primitivesColors.red["1000"],
    blue_primary: primitivesColors.blue["100"],
    blue_secondary: primitivesColors.blue["700"],
    blue_tertiary: primitivesColors.blue["1000"],
    green_primary: primitivesColors.green["100"],
    green_secondary: primitivesColors.green["700"],
    green_tertiary: primitivesColors.green["1000"],
    emerald_primary: primitivesColors.emerald["100"],
    emerald_secondary: primitivesColors.emerald["700"],
    emerald_tertiary: primitivesColors.emerald["1000"],
    orange_primary: primitivesColors.orange["100"],
    orange_secondary: primitivesColors.orange["700"],
    orange_tertiary: primitivesColors.orange["1000"],
    yellow_primary: primitivesColors.yellow["100"],
    yellow_secondary: primitivesColors.yellow["700"],
    yellow_tertiary: primitivesColors.yellow["1000"],
    rosewood_primary: primitivesColors.rosewood["100"],
    rosewood_secondary: primitivesColors.rosewood["700"],
    rosewood_tertiary: primitivesColors.rosewood["1000"],
    purple_primary: primitivesColors.purple["100"],
    purple_secondary: primitivesColors.purple["700"],
    purple_tertiary: primitivesColors.purple["1100"],
    grey_alpha_primary: alphaColors.grey["100"],
    grey_alpha_secondary: alphaColors.grey["700"],
    grey_alpha_tertiary: alphaColors.grey["1000"],
    white_alpha_primary: alphaColors.white["100"],
    white_alpha_secondary: alphaColors.white["700"],
    white_alpha_tertiary: alphaColors.white["1000"],
  },
  bg: {
    red_primary: primitivesColors.red["1000"],
    red_secondary: primitivesColors.red["700"],
    red_tertiary: primitivesColors.red["100"],
    blue_primary: primitivesColors.blue["1000"],
    blue_secondary: primitivesColors.blue["700"],
    blue_tertiary: primitivesColors.blue["100"],
    green_primary: primitivesColors.green["1000"],
    green_secondary: primitivesColors.green["700"],
    green_tertiary: primitivesColors.green["100"],
    emerald_primary: primitivesColors.emerald["1000"],
    emerald_secondary: primitivesColors.emerald["700"],
    emerald_tertiary: primitivesColors.emerald["100"],
    orange_primary: primitivesColors.orange["1000"],
    orange_secondary: primitivesColors.orange["700"],
    orange_tertiary: primitivesColors.orange["100"],
    yellow_primary: primitivesColors.yellow["1000"],
    yellow_secondary: primitivesColors.yellow["700"],
    yellow_tertiary: primitivesColors.yellow["100"],
    rosewood_primary: primitivesColors.rosewood["1000"],
    rosewood_secondary: primitivesColors.rosewood["700"],
    rosewood_tertiary: primitivesColors.rosewood["100"],
    purple_primary: primitivesColors.purple["1100"],
    purple_secondary: primitivesColors.purple["700"],
    purple_tertiary: primitivesColors.purple["100"],
    grey_alpha_primary: alphaColors.grey["1000"],
    grey_alpha_secondary: alphaColors.grey["700"],
    grey_alpha_tertiary: alphaColors.grey["100"],
    white_alpha_primary: alphaColors.white["1000"],
    white_alpha_secondary: alphaColors.white["700"],
    white_alpha_tertiary: alphaColors.white["100"],
  },
  border: {
    red_primary: primitivesColors.red["200"],
    red_secondary: primitivesColors.red["700"],
    red_tertiary: primitivesColors.red["900"],
    blue_primary: primitivesColors.blue["200"],
    blue_secondary: primitivesColors.blue["700"],
    blue_tertiary: primitivesColors.blue["900"],
    green_primary: primitivesColors.green["200"],
    green_secondary: primitivesColors.green["700"],
    green_tertiary: primitivesColors.green["900"],
    emerald_primary: primitivesColors.emerald["200"],
    emerald_secondary: primitivesColors.emerald["700"],
    emerald_tertiary: primitivesColors.emerald["900"],
    orange_primary: primitivesColors.orange["200"],
    orange_secondary: primitivesColors.orange["700"],
    orange_tertiary: primitivesColors.orange["900"],
    yellow_primary: primitivesColors.yellow["200"],
    yellow_secondary: primitivesColors.yellow["700"],
    yellow_tertiary: primitivesColors.yellow["900"],
    rosewood_primary: primitivesColors.rosewood["200"],
    rosewood_secondary: primitivesColors.rosewood["700"],
    rosewood_tertiary: primitivesColors.rosewood["900"],
    purple_primary: primitivesColors.purple["200"],
    purple_secondary: primitivesColors.purple["700"],
    purple_tertiary: primitivesColors.purple["1000"],
    grey_alpha_primary: alphaColors.grey["200"],
    grey_alpha_secondary: alphaColors.grey["700"],
    grey_alpha_tertiary: alphaColors.grey["900"],
    white_alpha_primary: alphaColors.white["200"],
    white_alpha_secondary: alphaColors.white["700"],
    white_alpha_tertiary: alphaColors.white["900"],
  },
};

export { stateColors, semanticColors, alphaColors, utilityColors };
